import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function AdminCategoriesTab() {
    const [cats, setCats] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ slug: "", name: "", name_en: "", color: "#9FE80D", image_url: "", icon: "sparkles" });

    const load = async () => {
        const { data } = await api.get("/categories");
        setCats(data);
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            await api.post("/admin/categories", form);
            toast.success("Categoría creada");
            setForm({ slug: "", name: "", name_en: "", color: "#9FE80D", image_url: "", icon: "sparkles" });
            setCreating(false);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        }
    };

    const del = async (c) => {
        try {
            await api.delete(`/admin/categories/${c.id}`);
            toast.success("Categoría eliminada");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-sora font-bold text-xl">Categorías</h3>
                <Dialog open={creating} onOpenChange={setCreating}>
                    <DialogTrigger asChild>
                        <Button data-testid="create-category-btn" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold">
                            <Plus className="w-4 h-4 mr-1.5" /> Nueva categoría
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                        <DialogHeader>
                            <DialogTitle className="font-sora">Crear categoría</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <Field label="Slug" testid="cat-slug-input" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} />
                            <Field label="Nombre (ES)" testid="cat-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                            <Field label="Nombre (EN)" testid="cat-name-en-input" value={form.name_en} onChange={(v) => setForm({ ...form, name_en: v })} />
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Icono</Label>
                                    <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                                        <SelectTrigger className="mt-2 bg-p2w-elevated border-p2w-line text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                                            {["sparkles", "trophy", "trending-up", "landmark", "cpu", "music"].map((i) => (
                                                <SelectItem key={i} value={i}>{i}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Color</Label>
                                    <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-2 bg-p2w-elevated border-p2w-line h-10 p-1" />
                                </div>
                            </div>
                            <Field label="URL imagen (opcional)" testid="cat-img-input" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setCreating(false)} className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line">Cancelar</Button>
                            <Button onClick={create} data-testid="confirm-create-category" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold">Crear</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cats.map((c) => (
                    <CategoryCard key={c.id} c={c} onDelete={() => del(c)} />
                ))}
            </div>
        </div>
    );
}

function CategoryCard({ c, onDelete }) {
    return (
        <div data-testid={`admin-category-${c.slug}`} className="relative overflow-hidden rounded-2xl border border-p2w-line bg-p2w-surface p-5">
            <div className="flex items-start justify-between">
                <div>
                    <div className="w-10 h-10 rounded-xl border" style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}40` }} />
                    <h4 className="mt-3 font-sora font-bold text-lg">{c.name}</h4>
                    <div className="text-xs text-p2w-gray">
                        slug: <code className="text-p2w-green">{c.slug}</code>
                    </div>
                    {c.name_en && <div className="text-xs text-p2w-gray">EN: {c.name_en}</div>}
                </div>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <button data-testid={`delete-category-${c.slug}`} className="p-2 rounded-lg text-p2w-red hover:bg-p2w-red/10 transition">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-p2w-surface border-p2w-line text-white">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="font-sora">¿Eliminar "{c.name}"?</AlertDialogTitle>
                            <AlertDialogDescription className="text-p2w-gray">
                                Solo se puede eliminar si no hay mercados asociados.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="bg-p2w-elevated border-p2w-line text-white hover:bg-p2w-line hover:text-white">Cancelar</AlertDialogCancel>
                            <AlertDialogAction className="bg-p2w-red text-white hover:bg-p2w-red/90" onClick={onDelete}>Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, testid }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-widest text-p2w-gray">{label}</Label>
            <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40" />
        </div>
    );
}
